import { Table } from '../../../../lib/ui/table/components/table/Table'
import React, { useEffect, useState } from 'react'
import { TrademarkInfringementsTableExpandRow } from '../trademark-infringements-table-expand-row/TrademarkInfringementsTableExpandRow'
import { useAppDispatch, useAppSelector } from '../../../../app/store/hooks/Redux'
import {
  selectTrademarkInfringementsExpandedRows,
  selectTrademarkInfringementsFilters,
  selectTrademarkInfringementsPagination,
  setExpandedRows,
  setTabCount,
  setTrademarkInfringementsPagination,
  TTabCount,
} from '../../model/trademarkInfringementsSlice'
import { BpButton } from '../../../../shared/components/buttons/bp-button/BpButton'
import { useTranslation } from 'react-i18next'
import { LatestAdExampleCell } from '../../ui/cells/LatestAdExampleCell'
import { LatestComplaintCell } from '../../ui/cells/LatestComplaintCell'
import { LastFoundCell } from '../../ui/cells/LastFoundCell'
import { ITrademarkInfringementsFilters } from '../../model/types/TrademarkInfringementsFilters.interface'
import { setModal } from '../../../../app/store/reducers/AppSlice'
import { TrademarkInfringementsConfirmModal } from '../trademark-infringements-confirm-modal/TrademarkInfringementsConfirmModal'
import { IconPaperAirplane } from '../../../../shared/components/icons/IconPaperAirplane'
import { CountryCell } from '../../ui/cells/CountryCell'
import { AdsCountCell } from '../../ui/cells/AdsCountCell'
import { DomainCell } from '../../ui/cells/DomainCell'
import { Tooltip } from 'antd'
import { ExportDrop } from '../../../../shared/components/cards/export-drop/ExportDrop'
import { IconUpload } from '../../../../shared/components/icons/IconUpload'
import { IconCheck } from '../../../../shared/components/icons/IconCheck'
import { BpTooltip } from '../../../../shared/components/cards/tooltip/BpTooltip'
import { TabSubmittedStub } from '../trademark-infringements-stubs/TabSubmittedStub'
import { TabAcceptedStub } from '../trademark-infringements-stubs/TabAcceptedStub'
import { TabRejectedStub } from '../trademark-infringements-stubs/TabRejectedStub'
import { TabRemovedStub } from '../trademark-infringements-stubs/TabRemovedStub'
import {
  useExportTmInfringements,
  useGetTmInfringementsListQuery,
  usePostTmInfringementsWhitelistMutation,
} from '../../../../app/api'

export const TrademarkInfringementsTable = () => {
  const { t } = useTranslation()

  const filters = useAppSelector<ITrademarkInfringementsFilters>(
    selectTrademarkInfringementsFilters,
  )
  const { current, pageSize } = useAppSelector(selectTrademarkInfringementsPagination)
  const expandedRows = useAppSelector(selectTrademarkInfringementsExpandedRows)

  const [selectedRows, setSelectedRows] = useState<any[]>([])
  const [queryParams, setQueryParams] = useState<any>({
    page: current,
    'per-page': pageSize,
  })
  const dispatch = useAppDispatch()

  const {
    data: infringementsList,
    isLoading,
    isFetching,
  } = useGetTmInfringementsListQuery(queryParams, {
    skip: !queryParams.projectId,
  })
  const [exportTrademarkInfringementsList, { isFetching: exportLoading }] =
    useExportTmInfringements()
  const [setWhitelist, { isLoading: whitelistLoading }] = usePostTmInfringementsWhitelistMutation()

  const selectedNonWhitelistDomains = Array.from(
    new Set(selectedRows.filter((row) => !row.is_whitelisted).map((row) => row.domain)),
  )
  const selectedWhitelistDomains = Array.from(
    new Set(selectedRows.filter((row) => row.is_whitelisted).map((row) => row.domain)),
  )

  const allRowKeys = infringementsList?.data?.items?.map((record) => record.infringement_id)
  const expandedRowKeys = expandedRows === 'all' ? allRowKeys : expandedRows

  const handleToggleExpandedRow = (key: number) => {
    const nextExpanded = expandedRowKeys?.includes(key)
      ? expandedRowKeys.filter((row) => row !== key)
      : [...(expandedRowKeys || []), key]

    dispatch(setExpandedRows(nextExpanded.length === allRowKeys?.length ? 'all' : nextExpanded))
  }

  useEffect(() => {
    setQueryParams({
      ...queryParams,
      projectId: filters?.project,
      countryId: filters?.country,
      site: filters?.site,
      date: filters.dateRange,
      trademarkId: filters?.trademark,
      caseId: filters?.caseId,
      showWhitelisted: filters?.showWhitelisted,
      tab: filters?.tab,
      onlyRegistered: filters?.onlyRegistered,
      ...(filters?.tab === 'in_progress' && { status: filters?.status }),
    })
  }, [filters])

  useEffect(() => {
    setSelectedRows([])
  }, [filters.tab])

  useEffect(() => {
    if (infringementsList?.data?.tab_count)
      dispatch(setTabCount(infringementsList.data.tab_count as TTabCount))
  }, [infringementsList])

  // Resolved: объявление уже снято или исчезло, отправлять жалобу отсюда нельзя
  const isResolved = filters.tab === 'removed'

  const columns = [
    {
      title: t('table.columns.domain'),
      dataIndex: 'domain',
      render: (_: string, record: any) => <DomainCell project={filters.project} {...record} />,
      key: 'domain',
    },
    {
      title: t('table.columns.country'),
      dataIndex: 'country',
      render: (_: string, record: any) => <CountryCell project={filters.project} {...record} />,
      key: 'country',
    },
    {
      title: t('table.columns.ads'),
      dataIndex: 'ads_count',
      key: 'ads_count',
      render: (_: number, record: any) => <AdsCountCell project={filters.project} {...record} />,
      sorter: true,
    },
    {
      title: t('table.columns.trademark'),
      dataIndex: 'trademark',
      key: 'trademark',
      render: (_: string, record: any) => <span>{record.trademark.name}</span>,
      sorter: true,
    },
    {
      title: t('table.columns.latestAdExample'),
      dataIndex: 'latest_ad_example',
      key: 'latest_ad_example',
      render: (_: string, record: any) => <LatestAdExampleCell {...record} />,
      width: 292,
    },
    {
      title: t('table.columns.lastFound'),
      dataIndex: 'last_found',
      key: 'last_found',
      render: (value: string) => <LastFoundCell lastFound={value} />,
      width: 180,
    },
    {
      title: isResolved ? (
        <span className={'flex items-center gap-[8px]'}>
          {t('table.columns.adStatus')}
          <BpTooltip title={t('pages.trademarkInfringements.adStatusTooltip')} />
        </span>
      ) : (
        t('table.columns.latestComplaint')
      ),
      dataIndex: 'complaint_status',
      key: 'complaint_status',
      width: 180,
      render: (_: string, record: any) => (
        <LatestComplaintCell {...record} onExpand={handleToggleExpandedRow} />
      ),
    },
    {
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_: undefined, record: any) =>
        record.complaint_status !== 'accepted' ? (
          <div className={'flex justify-end'}>
            <Tooltip
              title={
                !isResolved &&
                (!record.is_registered || record.is_whitelisted
                  ? t('pages.trademarkInfringements.addRegistrationNumberTooltip')
                  : record.complaint_status === 'submitted' &&
                    t('pages.trademarkInfringements.complaintPendingTooltip'))
              }
              placement={'bottomRight'}
            >
              <div>
                <BpButton
                  disabled={
                    isResolved ||
                    !record.is_registered ||
                    record.is_whitelisted ||
                    record.complaint_status === 'submitted' ||
                    record.complaint_status === 'removed'
                  }
                  size={'sm'}
                  onClick={() =>
                    dispatch(
                      setModal({
                        children: (
                          <TrademarkInfringementsConfirmModal
                            infringements={[record]}
                            project={filters.project}
                          />
                        ),
                        size: 'lg',
                      }),
                    )
                  }
                >
                  {record.complaint_status === 'rejected' || record.complaint_status === 'submitted'
                    ? t('buttons.resubmit')
                    : t('buttons.submit')}
                </BpButton>
              </div>
            </Tooltip>
          </div>
        ) : null,
    },
  ]

  const zeroStateStub = {
    to_submit: <TabSubmittedStub />,
    in_progress: {
      submitted: <TabSubmittedStub />,
      accepted: <TabAcceptedStub />,
      rejected: <TabRejectedStub project={filters.project} />,
    }[filters.status ?? 'submitted'],
    removed: <TabRemovedStub />,
  }[filters.tab]

  const canSubmit =
    isResolved ||
    selectedRows.find(
      (row) =>
        !row.is_registered ||
        row.is_whitelisted ||
        row.complaint_status === 'submitted' ||
        row.complaint_status === 'removed',
    )

  return (
    <div className={'h-full'}>
      <Table
        expandedRowRender={(record: any) => (
          <TrademarkInfringementsTableExpandRow record={record} />
        )}
        toolbar={
          selectedRows.length ? (
            <div className={'flex items-center justify-between'}>
              <div className={'flex items-center gap-[24px]'}>
                <p className={'font-medium text-[#6684A7]'}>
                  {t('other.countSelected', { count: selectedRows.length })}
                </p>
                <div className={'flex items-center gap-[24px]'}>
                  <Tooltip
                    title={
                      canSubmit &&
                      !isResolved &&
                      (filters.tab === 'in_progress'
                        ? t('pages.trademarkInfringements.complaintPendingTooltip')
                        : t('pages.trademarkInfringements.addRegistrationNumberTooltip'))
                    }
                  >
                    <div>
                      <button
                        className={
                          'flex items-center gap-[6px] text-[#A059FF] disabled:cursor-not-allowed disabled:opacity-50'
                        }
                        disabled={canSubmit}
                        onClick={() =>
                          dispatch(
                            setModal({
                              children: (
                                <TrademarkInfringementsConfirmModal
                                  infringements={selectedRows}
                                  project={filters.project}
                                />
                              ),
                              size: 'lg',
                            }),
                          )
                        }
                      >
                        <div className={'w-[11px]'}>
                          <IconPaperAirplane color={'#A059FF'} />
                        </div>
                        {filters.tab === 'in_progress'
                          ? t('buttons.resubmit')
                          : t('buttons.submit')}
                      </button>
                    </div>
                  </Tooltip>
                  {!!selectedNonWhitelistDomains.length && (
                    <button
                      className={
                        'flex items-center gap-[6px] text-[#A059FF] disabled:cursor-not-allowed disabled:opacity-50'
                      }
                      disabled={whitelistLoading}
                      onClick={() =>
                        setWhitelist({
                          body: {
                            project_id: filters.project as unknown as number,
                            domain: selectedNonWhitelistDomains.join(',') as unknown as string[],
                            is_whitelisted: true,
                          },
                        }).then(() => setSelectedRows([]))
                      }
                    >
                      <IconCheck className={'w-[11px]'} color={'#A059FF'} />
                      {t('pages.trademarkInfringements.addToWhitelist')}
                    </button>
                  )}
                  {!!selectedWhitelistDomains.length && (
                    <button
                      className={
                        'flex items-center gap-[6px] text-[#A059FF] disabled:cursor-not-allowed disabled:opacity-50'
                      }
                      disabled={whitelistLoading}
                      onClick={() =>
                        setWhitelist({
                          body: {
                            project_id: filters.project as unknown as number,
                            domain: selectedWhitelistDomains.join(',') as unknown as string[],
                            is_whitelisted: false,
                          },
                        }).then(() => setSelectedRows([]))
                      }
                    >
                      <IconCheck className={'w-[11px]'} color={'#A059FF'} />
                      {t('pages.trademarkInfringements.removeFromWhitelist')}
                    </button>
                  )}
                  <ExportDrop
                    columns={[
                      { label: t('table.columns.domain'), keys: ['domain'] },
                      { label: t('table.columns.country'), keys: ['country'] },
                      { label: t('table.columns.ads'), keys: ['ads_count'] },
                      { label: t('table.columns.trademark'), keys: ['trademark.name'] },
                      { label: t('table.columns.latestAdExample'), keys: ['latest_ad'] },
                      { label: t('table.columns.lastFound'), keys: ['last_found'] },
                      { label: t('table.columns.latestComplaint'), keys: ['complaint_status'] },
                    ]}
                    renderTrigger={({ open, onToggle, disabled }) => (
                      <button
                        aria-expanded={open}
                        className={'flex items-center gap-[6px] text-[#A059FF]'}
                        disabled={disabled}
                        type='button'
                        onClick={onToggle}
                      >
                        <div className={'w-[12px]'}>
                          <IconUpload color={'#A059FF'} />
                        </div>
                        {t('buttons.export')}
                      </button>
                    )}
                    dropPlacement={'top'}
                    loading={exportLoading}
                    onExport={(exportParams) =>
                      exportTrademarkInfringementsList({
                        ...exportParams,
                        projectId: Number(queryParams.projectId),
                        ids: selectedRows.map((row) => row.infringement_id) as unknown as string,
                      }).then(() => setSelectedRows([]))
                    }
                  />
                </div>
              </div>
              <button className={'text-[#DC2F02]'} onClick={() => setSelectedRows([])}>
                {t('buttons.clearSelection')}
              </button>
            </div>
          ) : null
        }
        columns={columns}
        data={infringementsList}
        emptyStub={zeroStateStub}
        expandedRowKeys={expandedRowKeys}
        loading={isLoading || isFetching}
        rowClassName={(e) => (e.is_whitelisted ? 'opacity-50' : '')}
        rowKey={(record) => record.infringement_id}
        selectedRowKeys={selectedRows}
        // между тулбаром страницы и таблицей gap 8px
        stubGap={8}
        onPaginationChange={(page, pageSize, total) => {
          dispatch(
            setTrademarkInfringementsPagination({
              current: page,
              pageSize: pageSize,
              total: total,
            }),
          )
          setQueryParams({
            ...queryParams,
            page: page,
            'per-page': pageSize,
          })
        }}
        onRowSelectionChange={(selection) => {
          setSelectedRows(selection)
        }}
        onSortingChange={(sorter) => {
          setQueryParams({
            ...queryParams,
            sort: sorter,
          })
        }}
      />
    </div>
  )
}
